<template>
  <div
    ref="revealTarget"
    class="product-card-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <article class="product-card">
      <nuxt-link class="product-card-media" :to="productPath">
        <site-media
          :source="product.cover_url"
          :alt="product.name"
          fit="contain"
          fallback-text="产品图片暂不可用"
        />
      </nuxt-link>
      <div class="product-card-copy">
        <h2>
          <nuxt-link :to="productPath">{{ product.name }}</nuxt-link>
        </h2>
        <p v-if="product.summary">
          {{ product.summary }}
        </p>
        <div class="product-card-footer">
          <strong v-if="product.price" class="product-price">
            ¥{{ product.price }}
            <small v-if="product.unit">/{{ product.unit }}</small>
          </strong>
          <span v-else class="product-price is-empty">价格请咨询</span>
          <nuxt-link class="product-detail-link" :to="productPath">
            查看详情
            <i class="i-lucide-chevron-right" aria-hidden="true"></i>
          </nuxt-link>
        </div>
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
  import type { ProductItem } from '~/types/site-api'

  interface Props {
    product: ProductItem
  }

  defineOptions({
    name: 'ProductCard',
  })

  const props = defineProps<Props>()

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.1)

  const productPath = computed(() => `/mall/${encodeURIComponent(props.product.id)}`)
</script>

<style scoped lang="scss">
  .product-card-reveal.is-reveal-ready:not(.is-revealed) {
    opacity: 0;
    transform: translateY(24px);
  }

  .product-card-reveal.is-reveal-ready.is-revealed {
    opacity: 1;
    transform: none;
    transition:
      opacity 520ms var(--ease),
      transform 520ms var(--ease);
  }

  .product-card {
    min-width: 0;
    height: 100%;
    overflow: hidden;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
    transition:
      transform var(--normal) var(--ease),
      border-color var(--normal) var(--ease),
      box-shadow var(--normal) var(--ease);
  }

  .product-card:hover,
  .product-card:focus-within {
    border-color: rgb(200 32 42 / 34%);
    box-shadow: 0 18px 40px rgb(45 31 32 / 14%);
    transform: translateY(-4px);
  }

  .product-card-media {
    display: block;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    background: #f1f2f3;
  }

  .product-card-media :deep(img) {
    padding: 20px;
    transition: transform var(--normal) var(--ease);
  }

  .product-card:hover .product-card-media :deep(img) {
    transform: scale(1.035);
  }

  .product-card-copy {
    display: flex;
    flex-direction: column;
    height: 170px;
    padding: 18px;
  }

  .product-card-copy h2 {
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 20px;
    line-height: 1.42;
    white-space: nowrap;
  }

  .product-card-copy h2 a:hover {
    color: var(--accent);
  }

  .product-card-copy p {
    display: -webkit-box;
    margin: 10px 0 0;
    overflow: hidden;
    -webkit-line-clamp: 2;
    font-size: 14px;
    line-height: 1.75;
    color: var(--muted);
    -webkit-box-orient: vertical;
  }

  .product-card-footer {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    justify-content: space-between;
    padding-top: 18px;
    margin-top: auto;
  }

  .product-price {
    font: 650 22px/1 var(--num);
    color: var(--accent);
  }

  .product-price small {
    margin-left: 3px;
    font-size: 12px;
    font-weight: 500;
    color: var(--muted);
  }

  .product-price.is-empty {
    font: 500 13px/1.4 var(--font);
    color: var(--muted);
  }

  .product-detail-link {
    display: inline-flex;
    gap: 4px;
    align-items: center;
    padding: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--accent);
    cursor: pointer;
    background: transparent;
    border: 0;
  }

  .product-detail-link i {
    width: 15px;
    height: 15px;
    transition: transform var(--fast) var(--ease);
  }

  .product-detail-link:hover i {
    transform: translateX(3px);
  }

  @media (max-width: 767px) {
    .product-card {
      height: auto;
      box-shadow: 0 6px 18px rgb(45 31 32 / 6%);
    }

    .product-card-copy {
      height: auto;
      padding: 14px 16px 16px;
    }

    .product-card-copy h2 {
      min-width: 0;
      font-size: 16px;
    }

    .product-card-copy h2 a {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .product-card-copy p {
      -webkit-line-clamp: 2;
    }

    .product-card-footer {
      gap: 10px;
      padding-top: 14px;
    }

    .product-price {
      font-size: 18px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .product-card-reveal.is-reveal-ready:not(.is-revealed) {
      opacity: 1;
      transform: none;
    }
  }
</style>
